import React, { useState, useEffect } from 'react'
import axios from 'axios'
import Header from '../components/Header'
import MapComponent from '../components/MapComponent'
import { useNavigate } from 'react-router-dom'
import './GymImages.css'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

const getAuth = () =>
  document.cookie.replace(/(?:(?:^|.*;\s*)auth\s*=\s*([^;]*).*$)|^.*$/, "$1")

const FIELD_LABELS = {
  name: 'Gym Name',
  description: 'Description',
  addressLine1: 'Address Line 1',
  addressLine2: 'Address Line 2',
  city: 'City',
  state: 'State',
  country: 'Country',
  pinCode: 'Pin Code',
  latitude: 'Latitude',
  longitude: 'Longitude',
}

const MergedGymPage = () => {
  const [gymData, setGymData] = useState(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [modalData, setModalData] = useState({})
  const [images, setImages] = useState([])
  const [showMap, setShowMap] = useState(false)
  const [states, setStates] = useState([])
  const [cities, setCities] = useState([])
  const [lat, setLat] = useState("")
  const [lng, setLng] = useState("")
  const navigate = useNavigate()
  const [showAlertModal, setShowAlertModal] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  const [uploading, setUploading] = useState(false)

  const fetchGymData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/fetch`, {
        headers: { auth: getAuth() }
      })
      setLat(response.data.gym.latitude)
      setLng(response.data.gym.longitude)
      setGymData(response.data.gym)
    } catch (error) {
      console.error('Error fetching gym data:', error)
    }
  }

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/gym-images`, {
        headers: { auth: getAuth() }
      })
      setImages(response.data)
    } catch (error) {
      console.error('Error fetching images:', error)
    }
  }

  const fetchStates = async () => {
    try {
      const response = await axios.post('https://countriesnow.space/api/v0.1/countries/states', { country: 'India' })
      setStates(response.data.data.states)
    } catch (error) {
      console.error('Error fetching states:', error)
    }
  }

  const fetchCities = async (state) => {
    try {
      const response = await axios.post('https://countriesnow.space/api/v0.1/countries/state/cities', {
        country: 'India', state,
      })
      setCities(response.data.data)
    } catch (error) {
      console.error('Error fetching cities:', error)
    }
  }

  useEffect(() => {
    fetchStates()
    fetchGymData()
    fetchImages()
  }, [])

  const handleShowEditModal = () => {
    setModalData({
      name: gymData?.name || '',
      description: gymData?.description || '',
      addressLine1: gymData?.addressLine1 || '',
      addressLine2: gymData?.addressLine2 || '',
      city: gymData?.city || '',
      state: gymData?.state || '',
      country: gymData?.country || '',
      pinCode: gymData?.pinCode || '',
      latitude: gymData?.latitude || '',
      longitude: gymData?.longitude || '',
    })
    fetchCities(gymData?.state)
    setShowEditModal(true)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name === 'state') fetchCities(value)
    setModalData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSave = () => {
    setShowMap(true)
  }

  const handleMapSave = async () => {
    try {
      await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/update`, modalData, {
        headers: { auth: getAuth() }
      })
      setShowEditModal(false)
      setShowMap(false)
      setAlertMessage('Gym updated successfully')
      setShowAlertModal(true)
      fetchGymData()
    } catch (error) {
      setAlertMessage('Error updating gym data')
      setShowAlertModal(true)
    }
  }

  const handleLocationSelect = (location) => {
    setLat(location.lat)
    setLng(location.lng)
    setModalData((prev) => ({ ...prev, latitude: location.lat, longitude: location.lng }))
  }

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        ({ coords: { latitude, longitude } }) => {
          setLat(latitude)
          setLng(longitude)
          setModalData((prev) => ({ ...prev, latitude, longitude }))
          toast.success('Location set to current position')
        },
        () => toast.error('Location access denied or unavailable')
      )
    } else {
      toast.error('Geolocation not supported by this browser')
    }
  }

  const handleImageUpload = async (event) => {
    const formData = new FormData()
    const files = event.target.files
    for (const file of files) {
      if (file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic')) {
        toast.error('HEIC format not supported. Please upload JPEG or PNG.')
        continue
      }
      formData.append('images', file)
    }
    setUploading(true)
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/gym-images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', auth: getAuth() },
      })
      fetchImages()
      toast.success('Images uploaded successfully')
    } catch (error) {
      toast.error('Error uploading images')
    } finally {
      setUploading(false)
    }
  }

  const handleImageDelete = async (imageId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/gym-images/${imageId}`, {
        headers: { auth: getAuth() }
      })
      fetchImages()
      toast.success('Image deleted')
    } catch (error) {
      toast.error('Error deleting image')
    }
  }

  return (
    <div className="gallery-page">
      <Header />
      <ToastContainer position="top-right" autoClose={3000} theme="dark" />

      <div className="gi-content">
        {/* Page Header */}
        <div className="gi-page-header">
          <div>
            <h1 className="gi-page-title">Gym Profile & Gallery</h1>
            <p className="gi-page-sub">Manage your gym details and photo gallery</p>
          </div>
          <div className="gi-nav-btns">
            <button className="gi-btn-ghost" onClick={() => navigate('/slots')}>← Slots</button>
            <button className="gi-btn-ghost" onClick={() => navigate('/subscription')}>Subscription →</button>
          </div>
        </div>

        <div className="gi-grid">
          {/* Gallery Section */}
          <div className="gi-card">
            <div className="gi-card-header">
              <div className="gi-card-icon">🖼️</div>
              <div>
                <h2 className="gi-card-title">Photo Gallery</h2>
                <p className="gi-card-sub">{images.length} photos</p>
              </div>
              <label className="gi-upload-btn">
                <input type="file" multiple onChange={handleImageUpload} style={{ display: 'none' }} />
                {uploading ? 'Uploading...' : '+ Upload Photos'}
              </label>
            </div>

            {images.length === 0 ? (
              <div className="gi-empty">
                <div className="gi-empty-icon">📷</div>
                <p>No photos yet. Upload images to showcase your gym.</p>
                <label className="gi-upload-btn-lg">
                  <input type="file" multiple onChange={handleImageUpload} style={{ display: 'none' }} />
                  Upload First Image
                </label>
              </div>
            ) : (
              <div className="gi-gallery">
                {images.map((image) => (
                  <div key={image.id} className="gi-gallery-item">
                    <img src={image.imageUrl} alt={`Gym ${image.id}`} />
                    <button className="gi-delete-btn" onClick={() => handleImageDelete(image.id)}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Gym Details Section */}
          <div className="gi-card">
            <div className="gi-card-header">
              <div className="gi-card-icon">🏋️</div>
              <div style={{ flex: 1 }}>
                <h2 className="gi-card-title">{gymData?.name || 'Your Gym'}</h2>
                {gymData?.gym_unique_id && <p className="gi-card-sub">ID: {gymData.gym_unique_id}</p>}
              </div>
              <button className="gi-btn-primary" onClick={handleShowEditModal}>Edit Details</button>
            </div>

            <div className="gi-details">
              {gymData?.description && (
                <div className="gi-detail-block">
                  <span className="gi-detail-label">Description</span>
                  <p className="gi-detail-value">{gymData.description}</p>
                </div>
              )}
              <div className="gi-detail-block">
                <span className="gi-detail-label">📍 Location</span>
                <p className="gi-detail-value">
                  {gymData?.addressLine1}{gymData?.addressLine2 ? `, ${gymData.addressLine2}` : ''}<br />
                  {[gymData?.city, gymData?.state, gymData?.country, gymData?.pinCode].filter(Boolean).join(', ')}
                </p>
              </div>
              {(gymData?.latitude || gymData?.longitude) && (
                <div className="gi-detail-row">
                  <div className="gi-detail-block">
                    <span className="gi-detail-label">Latitude</span>
                    <span className="gi-detail-coord">{gymData.latitude}</span>
                  </div>
                  <div className="gi-detail-block">
                    <span className="gi-detail-label">Longitude</span>
                    <span className="gi-detail-coord">{gymData.longitude}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="gi-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="gi-modal" onClick={e => e.stopPropagation()}>
            <div className="gi-modal-head">
              <h2>Edit Gym Details</h2>
              <button className="gi-modal-close" onClick={() => setShowEditModal(false)}>✕</button>
            </div>
            <div className="gi-modal-body">
              {Object.keys(modalData).map((key) => (
                <div className="gi-form-group" key={key}>
                  <label>{FIELD_LABELS[key] || key}</label>
                  {key === 'state' ? (
                    <select name="state" value={modalData.state} onChange={handleChange}>
                      <option value="">Select State</option>
                      {states.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
                    </select>
                  ) : key === 'city' ? (
                    <select name="city" value={modalData.city} onChange={handleChange} disabled={!modalData.state}>
                      <option value="">Select City</option>
                      {cities.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  ) : key === 'description' ? (
                    <textarea name={key} value={modalData[key]} onChange={handleChange} rows={3} />
                  ) : (
                    <input
                      type={key.includes('latitude') || key.includes('longitude') ? 'number' : 'text'}
                      name={key}
                      value={modalData[key]}
                      onChange={handleChange}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="gi-modal-foot">
              <button className="gi-btn-ghost" onClick={() => setShowEditModal(false)}>Cancel</button>
              <button className="gi-btn-primary" onClick={handleSave}>Set Location & Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Map Modal */}
      {showMap && (
        <div className="gi-modal-overlay" onClick={() => setShowMap(false)}>
          <div className="gi-modal gi-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gi-modal-head">
              <h2>Pin Your Gym Location</h2>
              <button className="gi-modal-close" onClick={() => setShowMap(false)}>✕</button>
            </div>
            <div className="gi-modal-body gi-map-body">
              <MapComponent
                onLocationSelect={handleLocationSelect}
                pinCode={modalData.pinCode}
                lat={lat}
                lng={lng}
                setCurrentLocation={handleUseCurrentLocation}
              />
            </div>
            <div className="gi-modal-foot">
              <button className="gi-btn-ghost" onClick={() => setShowMap(false)}>Cancel</button>
              <button className="gi-btn-primary" onClick={handleMapSave}>Save Location</button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      {showAlertModal && (
        <div className="gi-modal-overlay" onClick={() => setShowAlertModal(false)}>
          <div className="gi-modal gi-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="gi-modal-body" style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>
                {alertMessage.toLowerCase().includes('error') ? '❌' : '✅'}
              </div>
              <p style={{ color: '#cbd5e1', marginBottom: '20px', fontSize: '1rem' }}>{alertMessage}</p>
              <button className="gi-btn-primary" onClick={() => setShowAlertModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MergedGymPage
